import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from 'urql'
import type { FormVersions } from 'app/lib/forms/builder'
import {
  dependentsOf,
  duplicateQuestion,
  groupFormVersions,
  moveItem,
  newQuestion,
  parseQuestions,
  publishProblems,
  sameDraft,
} from 'app/lib/forms/builder'
import type { FormCategory, FormDefinition, FormQuestion, QuestionKind } from 'app/lib/forms/types'
import { useUnsavedChangesGuard } from 'app/hooks/useUnsavedChangesGuard'
import { buildBlock, type QuestionBlock } from 'app/lib/forms/questionBlocks'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import {
  DRAFT_NEXT_FORM_TEMPLATE_VERSION_MUTATION,
  FORM_TEMPLATE_QUERY,
  FORM_TEMPLATES_QUERY,
  PUBLISH_FORM_TEMPLATE_MUTATION,
  UPDATE_FORM_TEMPLATE_MUTATION,
} from 'app/components/paperwork/graphql'
import { FORM_EDITOR_PATH, type FormTemplateRow } from './useFormsList'

/** Long enough to let a word finish, short enough that closing the tab rarely loses anything. */
const AUTOSAVE_DELAY_MS = 1200

export type SaveState = `saved` | `saving` | `failed` | `needsName`

type Draft = { name: string; description: string; category: FormCategory; questions: FormQuestion[] }

const ALL_VERSIONS = { page: 0, limit: 100 }

function draftFrom(row: FormTemplateRow): Draft {
  return {
    name: row.name,
    description: row.description ?? ``,
    category: row.category as FormCategory,
    questions: parseQuestions(row.questionsJson),
  }
}

export type UseFormEditorReturn = {
  row: FormTemplateRow | undefined
  versions: FormVersions<FormTemplateRow> | undefined
  fetching: boolean
  notFound: boolean
  readOnly: boolean
  draft: Draft | undefined
  dirty: boolean
  saveState: SaveState
  busy: boolean
  problems: string[]
  preview: FormDefinition | undefined
  expandedId: string | undefined
  setExpandedId: (id: string | undefined) => void
  setField: <K extends keyof Omit<Draft, 'questions'>>(key: K, value: Draft[K]) => void
  addQuestion: (kind: QuestionKind) => void
  addBlock: (block: QuestionBlock) => void
  updateQuestion: (id: string, next: FormQuestion) => void
  moveQuestion: (index: number, to: number) => void
  removeQuestion: (id: string) => void
  duplicateQuestion: (id: string) => void
  publish: () => Promise<void>
  editNewVersion: () => Promise<void>
  openVersion: (id: string) => void
  back: () => void
}

export function useFormEditor(id: string): UseFormEditorReturn {
  const navigate = useNavigate()
  const [{ data, fetching, error }] = useQuery({ query: FORM_TEMPLATE_QUERY, variables: { id } })
  const row = data?.formTemplate
  const [{ data: versionData }] = useQuery({
    query: FORM_TEMPLATES_QUERY,
    variables: { formId: row?.formId, archived: false, pagination: ALL_VERSIONS },
    pause: !row?.formId,
    requestPolicy: `cache-and-network`,
  })
  const [{ fetching: saving }, executeUpdate] = useMutation(UPDATE_FORM_TEMPLATE_MUTATION)
  const [{ fetching: publishing }, executePublish] = useMutation(PUBLISH_FORM_TEMPLATE_MUTATION)
  const [{ fetching: drafting }, executeDraftNext] = useMutation(DRAFT_NEXT_FORM_TEMPLATE_VERSION_MUTATION)

  const [draft, setDraft] = useState<Draft>()
  const [expandedId, setExpandedId] = useState<string>()
  const [saveFailed, setSaveFailed] = useState(false)

  // Seeds local state once per version. A refetch after save must not overwrite typing in flight.
  useEffect(() => {
    if (row) setDraft(draftFrom(row))
  }, [row?.id])

  const readOnly = row?.status === `published`
  const dirty = !!row && !!draft && !sameDraft(draft, draftFrom(row))
  const leaveTo = useUnsavedChangesGuard(dirty, `your changes to this form`)
  const problems = draft ? publishProblems(draft.name, draft.questions) : []
  const versions = groupFormVersions(versionData?.formTemplates.hits ?? []).find((v) => v.formId === row?.formId)

  const patchQuestions = (change: (questions: FormQuestion[]) => FormQuestion[]) =>
    setDraft((current) => current && { ...current, questions: change(current.questions) })

  const addBlock = (block: QuestionBlock) => {
    const added = buildBlock(block)
    patchQuestions((questions) => [...questions, ...added])
    setExpandedId(added[0]?.id)
  }

  const addQuestion = (kind: QuestionKind) => {
    const question = newQuestion(kind)
    patchQuestions((questions) => [...questions, question])
    setExpandedId(question.id)
  }

  // A question that loses its trigger shows unconditionally, which is the safe direction: the
  // patient sees one extra question rather than the office losing an answer it needed.
  const removeQuestion = (questionId: string) =>
    patchQuestions((questions) => {
      const orphaned = new Set(dependentsOf(questions, questionId).map((q) => q.id))
      return questions
        .filter((q) => q.id !== questionId)
        .map((q) => (orphaned.has(q.id) ? { ...q, showWhen: undefined } : q))
    })

  // Saved exactly as typed, so the saved copy and the draft compare equal afterwards and the page
  // settles on Saved. A blank name is the one thing not sent: the API needs one.
  const save = async (): Promise<boolean> => {
    if (!draft || readOnly || !draft.name.trim()) return false
    const { error: saveError } = await executeUpdate({
      id,
      input: {
        name: draft.name,
        description: draft.description,
        category: draft.category,
        questions: draft.questions,
      },
    })
    setSaveFailed(!!saveError)
    if (saveError) logError(`useFormEditor: save failed`, saveError.message)
    return !saveError
  }

  useEffect(() => {
    if (!dirty || readOnly) return
    const timer = setTimeout(() => bg(save()), AUTOSAVE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [draft, readOnly])

  // A failed save keeps the draft dirty, so failure is checked before pending edits or it would
  // read as saving forever. The next edit tries again.
  const saveState: SaveState =
    draft && !draft.name.trim() ? `needsName` : saving ? `saving` : saveFailed ? `failed` : dirty ? `saving` : `saved`

  const publish = async () => {
    if (problems.length > 0) return
    if (dirty && !(await save())) {
      notifyError(`Couldn't save the form, so it was not published`)
      return
    }
    const { error: publishError } = await executePublish({ id }, { additionalTypenames: [`FormTemplate`] })
    if (publishError) {
      notifyError(`Couldn't publish the form`)
      logError(`useFormEditor: publish failed`, publishError.message)
      return
    }
    notifySuccess(`Published. Packets can now use this version.`)
  }

  const editNewVersion = async () => {
    const { data: next, error: draftError } = await executeDraftNext({ id }, { additionalTypenames: [`FormTemplate`] })
    if (draftError || !next) {
      notifyError(`Couldn't start a new version`)
      logError(`useFormEditor: draft next version failed`, draftError?.message)
      return
    }
    navigate(`${FORM_EDITOR_PATH}/${next.draftNextFormTemplateVersion.id}`)
  }

  return {
    row,
    versions,
    fetching,
    notFound: !fetching && (!!error || !row),
    readOnly,
    draft,
    dirty,
    saveState,
    busy: saving || publishing || drafting,
    problems,
    preview: draft && row && { ...draft, id: row.formId, version: row.formVersion },
    expandedId,
    setExpandedId,
    setField: (key, value) => setDraft((current) => current && { ...current, [key]: value }),
    addQuestion,
    addBlock,
    updateQuestion: (questionId, next) =>
      patchQuestions((questions) => questions.map((q) => (q.id === questionId ? next : q))),
    moveQuestion: (index, to) => patchQuestions((questions) => moveItem(questions, index, to)),
    removeQuestion,
    duplicateQuestion: (questionId) => {
      const original = draft?.questions.find((q) => q.id === questionId)
      if (!original) return
      const copy = duplicateQuestion(original)
      patchQuestions((questions) => {
        const at = questions.findIndex((q) => q.id === questionId)
        return [...questions.slice(0, at + 1), copy, ...questions.slice(at + 1)]
      })
      setExpandedId(copy.id)
    },
    publish,
    editNewVersion,
    openVersion: (versionId) => leaveTo(`${FORM_EDITOR_PATH}/${versionId}`),
    back: () => leaveTo(FORM_EDITOR_PATH),
  }
}
